import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
} from '@tanstack/react-query';
import type {
  BackupBlob,
  BackupInfo,
} from '@internal/backstage-plugin-bidone-backend';
import { UseQueryCustomResult } from '../types';
import { useAuthedFetch } from './useAuthedFetch';
import {
  RELEASES_KEY,
  RELEASE_BACKUPS_KEY,
  checklistKey,
} from './useReleaseChecklist/keys';

export const useReleaseChecklistBackups = (): UseQueryCustomResult<
  BackupBlob[]
> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: RELEASE_BACKUPS_KEY,
    queryFn: async () => authedFetch('/releases/backups'),
  });
};

export const useReleaseChecklistBackupInfo =
  (): UseQueryCustomResult<BackupInfo> => {
    const authedFetch = useAuthedFetch();
    return useQuery({
      queryKey: ['release-checklist', 'backups', 'info'] as const,
      queryFn: async () => authedFetch('/releases/backups/info'),
      // Container metadata (region/env/quota/portal URL) comes from
      // app-config and only changes on backend restart — cache
      // aggressively so we don't refetch on every page navigation.
      staleTime: 60 * 60 * 1000,
    });
  };

type RunResult = { partitions: number; bytes: number };

export const useRunReleaseChecklistBackup = (): UseMutationResult<
  RunResult,
  Error,
  void
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      authedFetch('/releases/backups/run', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RELEASE_BACKUPS_KEY });
    },
  });
};

type DeleteArgs = { blobNames: string[] };
type DeleteResult = { deleted: number };

export const useDeleteReleaseChecklistBackups = (): UseMutationResult<
  DeleteResult,
  Error,
  DeleteArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ blobNames }) =>
      authedFetch('/releases/backups/delete', {
        method: 'POST',
        json: { blobNames },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RELEASE_BACKUPS_KEY });
    },
  });
};

type RestoreArgs = { release: string; blobName: string };
type RestoreResult = { release: string; blobName: string; items: number };

export const useRestoreReleaseChecklistBackup = (): UseMutationResult<
  RestoreResult,
  Error,
  RestoreArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ release, blobName }) =>
      authedFetch('/releases/backups/restore', {
        method: 'POST',
        json: { release, blobName },
      }),
    onSuccess: data => {
      // The restored release's checklist + the list of releases may both
      // have changed (a restored release that no longer exists in the
      // live table reappears). Drop both caches.
      qc.removeQueries({ queryKey: checklistKey(data.release) });
      qc.invalidateQueries({ queryKey: RELEASES_KEY });
    },
  });
};
